import React, { useEffect, useMemo, useState } from "react"
import { Container, Row, Col, Modal, Label, Input } from "reactstrap"
import Select from "react-select"
import axios from "axios"
import Swal from "sweetalert2"
import moment from "moment"
import { useSelector } from "react-redux"
import Breadcrumbs from "components/Common/Breadcrumb"
import Loader from "pages/Separate/Loader"
import secureLocalStorage from "react-secure-storage"
import configure from "configure"
import { fetchNodeData, FetchNodePostData } from "utils/common-fetchData"
import { colourStyles } from "utils/common-options"
import "./bankStatementConsole.css"
import StatementExportModal from "./StatementExportModal"
import {
    AccountInfoLine,
    AccountOptionLabel,
    buildSuggestedGroups,
    formatInr,
    formatMatchOption,
    toAccountCounts,
    toExpenseOption,
} from "./statementMatchHelpers"
// import { STATEMENT_MODULES, useStatementPermissions } from "./statementEntryActions" — role access disabled for now
import { useStatementEntryActions } from "./statementEntryActions"

// What an entry can be matched to depends on which side of the statement it's
// on. A credit row is money that came INTO the bank, a debit row is money
// that went OUT — so "Overdraft (Withdraw)" (drawing cash into our account)
// only makes sense on a credit row, and "Overdraft (Deposit)" (paying the OD
// back down) only makes sense on a debit row. Shareholder movements are
// acknowledgement-only in both directions — they never create a real ledger
// entry anywhere else, this bank statement row IS the record.
const CREDIT_MATCH_TYPES = [
    { value: "invoice", label: "Invoice Payment" },
    { value: "overdraft_withdraw", label: "Overdraft (Withdraw)" },
    { value: "shareholder_investment", label: "Shareholder Investment" },
]
const DEBIT_MATCH_TYPES = [
    { value: "overdraft_deposit", label: "Overdraft (Deposit)" },
    { value: "shareholder_return", label: "Shareholder Return" },
    { value: "expense", label: "Expense" },
]
const SHAREHOLDER_TYPES = ["shareholder_investment", "shareholder_return"]

const MATCH_TYPE_LABELS = Object.fromEntries(
    [...CREDIT_MATCH_TYPES, ...DEBIT_MATCH_TYPES].map(t => [t.value, t.label])
)

const INVOICE_SOURCES = [
    { key: "zithas", label: "Zithas", endpoint: "zithasInvoice" },
    { key: "ztpl", label: "ZTPL", endpoint: "ztplInvoice" },
]

// Invoice row -> dropdown option. Statement rows are always INR, so a
// foreign-currency invoice also carries its due converted at the invoice's
// own stored rate (shown in the label, and what "suggested" ranks by).
const toInvoiceOption = (i, source) => {
    const due = Number(i.balance_due) || 0
    const total = Number(i.total) || 0
    const currency = i.currency_name || ""
    const symbol = i.currency_symbol || ""
    // Currency id 2 is INR — same test the backend uses when booking.
    const foreign = i.c_currency != null && String(i.c_currency).trim() !== "" && String(i.c_currency) !== "2" && currency !== "INR"
    const rate = Number(i.exchange_rate) > 0 ? Number(i.exchange_rate) : null
    const toInr = n => (foreign ? (rate ? n * rate : NaN) : n)
    const dueInr = toInr(due)
    const number = i.invoice_number || i.tax_inv_no || `#${i.id}`
    const label = `${number} - ${i.customer_name || ""} (Due: ${symbol}${due.toFixed(2)} ${currency}${foreign && rate ? ` ≈ ${formatInr(dueInr)}` : ""})`
    return {
        // Zithas and ZTPL ids overlap and both can sit in the Suggested group.
        value: `${source.key}-${i.id}`,
        id: i.id,
        source: source.key,
        due,
        currency,
        symbol,
        foreign,
        rate,
        dueInr,
        // A fully paid invoice is a Map candidate — compare its full total.
        cmpInr: Math.abs(due) > 0.01 ? dueInr : toInr(total),
        nameText: i.customer_name,
        label,
        suggestLabel: `[${source.label}] ${label}`,
    }
}

// The bank whose statement is being imported is now sourced from the real
// finance-modes list (payment_via === "1" marks a row as a bank account,
// vs cash/credit-card/etc). We keep BOTH the numeric id (for payment_mode_id
// / payment_method_id when creating expense/invoice records — it must be the
// tblpayment_finance.id, not the string "1") AND the lower-cased payment_mode
// label as bank_name for fetching / uploading statement rows. payment_via is
// always "1" for a bank statement row — the specific bank is payment_method_id.

const BankStatementConsole = () => {
    document.title = "Bank Statement Reconsile | Zithas Crm"
    const token = secureLocalStorage.getItem("auth_token")
    const [loading, setLoading] = useState(false)
    // Roles & Responsibilities: add = Import / Add Entry, edit = Edit,
    // delete = Delete. View is enforced by the route itself.
    // const { canAdd, canEdit, canDelete } = useStatementPermissions(STATEMENT_MODULES.bank) — role access disabled for now
    const canAdd = true
    const canEdit = true
    const canDelete = true

    const [bankOptions, setBankOptions] = useState([])
    const [selectedBank, setSelectedBank] = useState(null)
    const [exportModal, setExportModal] = useState(false)
    const [entries, setEntries] = useState([])
    const [file, setFile] = useState(null)
    // Bumped after each successful import so the file input itself clears —
    // otherwise the old filename stays visible and re-picking the same file
    // won't fire onChange again.
    const [fileKey, setFileKey] = useState(0)

    // Global counts (across every bank) so "how much is left" is visible even
    // before a bank is selected — the per-bank `entries` list can't answer
    // that on its own.
    const [summary, setSummary] = useState({ total: 0, matched: 0, unmatched: 0 })

    // Per-bank unmatched counts so the Bank dropdown itself shows how many
    // entries still need work per bank, before anything is selected.
    // Keyed by lower-cased bank_name (same key used for fetch/upload).
    const [bankCounts, setBankCounts] = useState({})

    // list toolbar / pagination — entries are now fetched a page at a time from
    // the server (some banks have far too many statement rows to load and
    // slice client-side in one shot), so `total`/`counts` come back from the
    // API alongside the current page's rows instead of being derived locally.
    const [activeTab, setActiveTab] = useState("unmatched") // unmatched | matched
    const [search, setSearch] = useState("")
    const [debouncedSearch, setDebouncedSearch] = useState("")
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [total, setTotal] = useState(0)
    const [tabCounts, setTabCounts] = useState({ matched: 0, unmatched: 0 })

    // Overdraft accounts — only used inside the Match modal's Overdraft option,
    // separate from the statement-format bank selected above.
    const [odBanks, setOdBanks] = useState([])

    const [matchModal, setMatchModal] = useState(false)
    const [activeEntry, setActiveEntry] = useState(null)
    const [matchType, setMatchType] = useState(null)

    // invoice fields
    const [invoiceSource, setInvoiceSource] = useState("zithas")
    // Both invoice families are loaded together (and kept until a match
    // changes a due) so the Suggested group can rank across Zithas AND ZTPL
    // whichever radio is selected. null = not loaded yet.
    const [invoiceLists, setInvoiceLists] = useState({ zithas: null, ztpl: null })
    const [selectedInvoice, setSelectedInvoice] = useState(null)
    // INR rate for the selected foreign-currency invoice on the payment date.
    const [fx, setFx] = useState({ loading: false, rate: null })

    // expense fields
    const [expenseAction, setExpenseAction] = useState("new")
    const [categoryOptions, setCategoryOptions] = useState([])
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [expenseTitle, setExpenseTitle] = useState("")
    const [expenseLocation, setExpenseLocation] = useState(null)
    const [expenseOptions, setExpenseOptions] = useState([])
    const [selectedExpense, setSelectedExpense] = useState(null)

    // overdraft fields — direction (withdraw vs deposit) now comes from which
    // matchType was picked, not a separate radio
    const [odAccount, setOdAccount] = useState(null)

    // invoice fields (continued)
    const [tds, setTds] = useState("")
    const [transactionRef, setTransactionRef] = useState("")

    // shareholder fields — acknowledgement only
    const [shareholderName, setShareholderName] = useState("")

    // shared fields
    const [amount, setAmount] = useState("")
    const [matchDate, setMatchDate] = useState("")
    const [notes, setNotes] = useState("")

    // Map-to-existing-entry (reconciliation) — "create" books a brand new
    // invoice payment/expense/OD row, "map" instead acknowledge-links this
    // statement row directly to the INVOICE / EXPENSE / OVERDRAFT ACCOUNT
    // itself (not to any specific payment record underneath it) — the link
    // on this bank statement row IS the record, same idea as the shareholder
    // acknowledgement types, just gated by due/limit instead of always on.
    // Invoice and overdraft are FORCED into map-only once their condition
    // hits (due ≈ 0 for invoice; over the account's limit for overdraft) —
    // there's nothing left to create once that's true, so no toggle is
    // shown. Expense stays a manual choice always: you can still add another
    // payment against an expense even after its due hits ₹0 (extra
    // reimbursement, adjustment, additional installment), so due only picks
    // a sensible default tab.
    const [matchMode, setMatchMode] = useState("create") // create | map

    let getLocations = useSelector((state) => state?.location?.locations)
    getLocations = useMemo(
        () => getLocations?.filter((l) => configure.location.includes(l.value)) || [],
        [getLocations]
    )

    const fetchOdBanks = async () => {
        const res = await fetchNodeData(setLoading, "overdraftBanks", token)
        if (res?.success) {
            setOdBanks(
                res?.data?.banks?.map(b => ({
                    value: b.id,
                    label: `${b.bank_name} - ${b.account_no}`,
                    ...b,
                })) || []
            )
        }
    }

    // Debounce the search box so it doesn't fire a server request on every
    // keystroke — 400ms after the user stops typing is enough to feel instant
    // without hammering the API while they're still mid-word.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 400)
        return () => clearTimeout(t)
    }, [search])

    const fetchEntries = async bankName => {
        if (!bankName) return
        const params = new URLSearchParams({
            page: String(page),
            limit: String(pageSize),
            status: activeTab,
        })
        // Numeric bank id lets the backend match new (bank_id) rows as well as
        // legacy (label-only) rows even if the bank label was renamed since.
        if (selectedBank?.value) params.set("bank_id", String(selectedBank.value))
        if (debouncedSearch) params.set("search", debouncedSearch)
        const res = await fetchNodeData(setLoading, `bankStatement/${bankName}?${params.toString()}`, token)
        // Accept the response by shape (an array under `data`) rather than
        // requiring `success: true` — some deployments of this endpoint have
        // been seen returning `{ data: [...] }` without the success flag, which
        // silently dropped every entry here even though the data was correct.
        if (Array.isArray(res?.data)) {
            setEntries(res.data)
            setTotal(res?.pagination?.total || 0)
            setTabCounts({ matched: res?.counts?.matched || 0, unmatched: res?.counts?.unmatched || 0 })
        }
    }

    const fetchSummary = async () => {
        const res = await fetchNodeData(() => { }, "bankStatement/summary/counts", token)
        if (res?.success) {
            setSummary({ total: res.total, matched: res.matched, unmatched: res.unmatched })
        }
    }

    const fetchBankCounts = async () => {
        const res = await fetchNodeData(() => { }, "bankStatement/summary/by-bank", token)
        if (res?.success && Array.isArray(res?.data)) {
            const map = {}
            res.data.forEach(r => {
                if (r?.bank_name) map[r.bank_name] = toAccountCounts(r)
            })
            setBankCounts(map)
        }
    }

    // Label for the Bank dropdown — shows each bank's own unmatched count and
    // last upload date so the admin can see what needs work before selecting
    // anything. Falls back to the bare label until the counts endpoint answers.
    const formatBankOptionLabel = (option, { context }) => (
        <AccountOptionLabel label={option?.label} counts={bankCounts?.[option?.bank_name]} context={context} />
    )

    useEffect(() => {
        fetchOdBanks()
        fetchSummary()
        fetchBankCounts()
        fetchNodeData(setLoading, "financeCategory", token).then(res => {
            if (res?.success) {
                setCategoryOptions(res?.data?.map(c => ({ value: c.id, label: c.name })) || [])
            }
        })
        // Bank list = finance modes flagged as a bank account (payment_via "1").
        // Keep both the numeric id (for payment_mode_id) and the lower-cased
        // payment_mode label as bank_name for statement fetch/upload. The
        // statement row itself is always payment_via=1 (Bank), the specific bank
        // is identified by its id.
        fetchNodeData(() => { }, "financeModes", token).then(res => {
            if (res?.success) {
                const banks = (res?.data || []).filter(m => String(m.payment_via) === "1")
                setBankOptions(
                    banks.map(m => ({
                        value: m.id,
                        label: m.payment_mode,
                        bank_name: (m.payment_mode || "").trim().toLowerCase(),
                        payment_via: String(m.payment_via),
                    }))
                )
            }
        })
    }, [])

    useEffect(() => {
        fetchEntries(selectedBank?.bank_name)
    }, [selectedBank, page, pageSize, activeTab, debouncedSearch])

    const onUpload = async () => {
        if (!selectedBank) {
            Swal.fire({ icon: "warning", text: "Please select a bank first." })
            return
        }
        if (!file) {
            Swal.fire({ icon: "warning", text: "Please choose an Excel file to upload." })
            return
        }
        setLoading(true)
        try {
            const formData = new FormData()
            formData.append("bank_name", selectedBank.bank_name)
            // Numeric tblpayment_finance id — lets the backend store bank_id on
            // new rows (and scope fetches) so a later rename of the bank label
            // doesn't orphan this statement history.
            formData.append("bank_id", selectedBank.value)
            formData.append("file", file)
            const res = await axios.post(`${configure.nodeApi}/bankStatement/upload`, formData, {
                headers: { Authorization: `Bearer ${token}` },
            })
            if (Array.isArray(res?.data?.data)) {
                Swal.fire({ icon: "success", text: res?.data?.message || "Upload complete" })
                setFile(null)
                setFileKey(k => k + 1)
                // Upload's own response is the bank's full unpaginated list (used
                // for the "N imported" count) — refetch through the paginated
                // endpoint instead of dumping that straight into `entries`.
                fetchEntries(selectedBank?.bank_name)
                fetchSummary()
                fetchBankCounts()
            } else {
                Swal.fire({ icon: "error", text: res?.data?.message || "Upload failed" })
            }
        } catch (err) {
            Swal.fire({ icon: "error", text: err?.response?.data?.message || err?.message })
        } finally {
            setLoading(false)
        }
    }

    const openMatchModal = entry => {
        setActiveEntry(entry)
        setMatchType(null)
        setInvoiceSource("zithas")
        setSelectedInvoice(null)
        setTds("")
        // Pre-fill from the bank statement's own reference number (cheque/UTR/etc)
        // — it's the natural transaction reference for an invoice payment created
        // from this row, but still editable in case it needs correcting.
        setTransactionRef(entry?.reference_no || "")
        setExpenseAction("new")
        setSelectedCategory(null)
        setExpenseTitle("")
        setExpenseLocation(null)
        setSelectedExpense(null)
        setExpenseOptions([])
        setOdAccount(null)
        setShareholderName("")
        setNotes("")
        setMatchMode("create")
        setAmount(Number(entry?.credit) > 0 ? entry?.credit : entry?.debit)
        setMatchDate(entry?.statement_date ? moment(entry.statement_date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"))
        setMatchModal(true)
    }

    const isCreditEntry = Number(activeEntry?.credit) > 0
    const matchTypeOptions = isCreditEntry ? CREDIT_MATCH_TYPES : DEBIT_MATCH_TYPES
    // The statement's own amount — what every due/limit check below compares
    // against, regardless of what's typed into the (editable, create-mode
    // only) Amount field above.
    const entryAmount = activeEntry ? (isCreditEntry ? Number(activeEntry.credit) : Number(activeEntry.debit)) || 0 : 0

    const loadInvoices = () => {
        INVOICE_SOURCES.filter(src => !invoiceLists[src.key]).forEach(src => {
            fetchNodeData(setLoading, src.endpoint, token).then(res => {
                if (res?.status == 200) {
                    const options = (res?.data || []).map(i => toInvoiceOption(i, src))
                    setInvoiceLists(lists => ({ ...lists, [src.key]: options }))
                }
            })
        })
    }

    const onSelectMatchType = type => {
        setMatchType(type)
        setMatchMode("create")
        if (type?.value === "invoice") loadInvoices()
        if (type?.value === "expense") {
            // Location 1 = the same set the old budgetExpense/2/1 call listed,
            // but complete — that endpoint only returns its first page of 50.
            fetchNodeData(setLoading, "bankStatement/expense/options?location=1", token).then(res => {
                if (res?.success) setExpenseOptions((res?.data || []).map(toExpenseOption))
            })
        }
    }

    const onChangeInvoiceSource = source => {
        setInvoiceSource(source)
        setSelectedInvoice(null)
    }

    // A suggestion can come from the other invoice family — follow it.
    const onPickInvoice = option => {
        if (option?.source && option.source !== invoiceSource) setInvoiceSource(option.source)
        setSelectedInvoice(option)
    }

    // Suggested-first dropdown lists: records closest in INR value to this
    // statement row (or named in its description) on top, everything after.
    const invoiceSelectOptions = useMemo(
        () =>
            buildSuggestedGroups(
                [...(invoiceLists.zithas || []), ...(invoiceLists.ztpl || [])],
                invoiceLists[invoiceSource] || [],
                entryAmount,
                activeEntry?.description,
                // Receipts usually land a little under the due: TDS, bank
                // charges, and FX movement since the invoice was raised.
                { tolerance: 0.12, allLabel: `All ${invoiceSource === "ztpl" ? "ZTPL" : "Zithas"} invoices` }
            ),
        [invoiceLists, invoiceSource, entryAmount, activeEntry]
    )
    const expenseSelectOptions = useMemo(
        () => buildSuggestedGroups(expenseOptions, expenseOptions, entryAmount, activeEntry?.description, { tolerance: 0.05, allLabel: "All registered expenses" }),
        [expenseOptions, entryAmount, activeEntry]
    )

    // Rate preview for a foreign-currency invoice — the backend converts the
    // INR statement amount at this same rate when the match is confirmed.
    useEffect(() => {
        if (!matchModal || matchMode !== "create" || !selectedInvoice?.foreign || !selectedInvoice.currency) {
            setFx({ loading: false, rate: null })
            return
        }
        let cancelled = false
        setFx({ loading: true, rate: null })
        const params = new URLSearchParams({ currency: selectedInvoice.currency, date: matchDate || "" })
        fetchNodeData(() => { }, `bankStatement/fx/preview?${params.toString()}`, token).then(res => {
            if (!cancelled) setFx({ loading: false, rate: Number(res?.rate) > 0 ? Number(res.rate) : null })
        })
        return () => {
            cancelled = true
        }
    }, [matchModal, matchMode, selectedInvoice, matchDate])

    // Invoice — no manual choice: due ≈ 0 means it's already paid elsewhere
    // and just needs reconciling, so force Map; otherwise force Create.
    useEffect(() => {
        if (!matchModal || matchType?.value !== "invoice") return
        setMatchMode(selectedInvoice && Math.abs(Number(selectedInvoice.due) || 0) < 0.01 ? "map" : "create")
    }, [matchModal, matchType, selectedInvoice])

    // Expense — "Add New Expense" always creates (nothing exists yet to
    // check). "Pay Registered Expense" only sets a DEFAULT from due when a
    // different expense is picked; the manual Add/Map toggle in the JSX can
    // still override it afterwards without this effect fighting back, since
    // it only re-fires when the expense selection itself changes.
    useEffect(() => {
        if (!matchModal || matchType?.value !== "expense") return
        if (expenseAction !== "existing" || !selectedExpense) {
            setMatchMode("create")
            return
        }
        setMatchMode(Math.abs(Number(selectedExpense.due) || 0) < 0.01 ? "map" : "create")
    }, [matchModal, matchType, expenseAction, selectedExpense])

    // Overdraft — no manual choice either: if this amount would breach the
    // account's limit (withdraw > available bal_limit, or return > used_limit
    // — including used_limit already ₹0, i.e. nothing left owed), it can't
    // legitimately be a NEW transaction, so force Map instead of the old hard
    // "exceeds limit" error.
    useEffect(() => {
        if (!matchModal || !odAccount) return
        if (matchType?.value !== "overdraft_withdraw" && matchType?.value !== "overdraft_deposit") return
        const isWithdraw = matchType.value === "overdraft_withdraw"
        const limit = Number(isWithdraw ? odAccount.bal_limit : odAccount.used_limit) || 0
        setMatchMode(entryAmount > limit ? "map" : "create")
    }, [matchModal, matchType, odAccount, entryAmount])

    // Shared POST for both "create" and "map" submissions. FetchNodePostData
    // swallows a non-2xx body, so the backend answers real failures (already
    // matched, invalid type, double-link) with 200 + success:false instead —
    // this is what actually surfaces those messages to the admin.
    const postMatch = async body => {
        const res = await FetchNodePostData(setLoading, "bankStatement/match", token, body)
        const data = res?.data
        if (data?.success) {
            Swal.fire({ icon: "success", text: "Entry matched successfully." })
            setMatchModal(false)
            // Dues just changed — reload invoices next time the modal needs them.
            setInvoiceLists({ zithas: null, ztpl: null })
            fetchEntries(selectedBank?.bank_name)
            fetchSummary()
            fetchBankCounts()
            return
        }
        Swal.fire({ icon: "error", text: data?.message || "Could not match this entry." })
    }

    // The whole "map" action in one click: links this statement row directly
    // to the invoice/expense/overdraft-account already selected above — no
    // payment-record search or picking involved. One explanatory confirm
    // dialog names exactly what this links to before it submits.
    const onMapToTarget = async () => {
        if (!matchType) return
        let label, targetBody
        if (matchType.value === "invoice") {
            if (!selectedInvoice) return
            label = selectedInvoice.label
            targetBody = { invoice_type: invoiceSource, invoice_id: selectedInvoice.id }
        } else if (matchType.value === "expense") {
            if (!selectedExpense) return
            label = selectedExpense.label
            targetBody = { expense_id: selectedExpense.value }
        } else if (matchType.value === "overdraft_withdraw" || matchType.value === "overdraft_deposit") {
            if (!odAccount) return
            label = odAccount.label
            targetBody = { overdraft_bank_id: odAccount.value }
        } else {
            return
        }

        const confirmResult = await Swal.fire({
            title: "Map this statement row?",
            html: `Link this statement row to <b>${label}</b>?<br/>No new payment will be created — this only records the link.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, map it",
        })
        if (!confirmResult.isConfirmed) return

        await postMatch({
            entry_id: activeEntry.id,
            matched_type: matchType.value,
            mode: "map",
            notes: notes || null,
            ...targetBody,
        })
    }

    const onSubmitMatch = async () => {
        if (!matchType) {
            Swal.fire({ icon: "warning", text: "Please select where this entry belongs." })
            return
        }
        if (!selectedBank) {
            Swal.fire({ icon: "warning", text: "Please select a bank again — the bank context was lost." })
            return
        }
        // Map submissions happen via onMapToTarget's own "Map" button — the
        // footer button is hidden whenever matchMode is "map" (see the footer
        // JSX), so reaching here in that state would mean the hidden button
        // was triggered some other way. Guard it anyway rather than silently
        // building a nonsensical create-mode body.
        if (matchMode === "map") {
            Swal.fire({ icon: "info", text: "Click the Map button above to complete this match." })
            return
        }
        // An empty/cleared Amount field would otherwise fall back to the full
        // statement amount backend-side (Number("" || entryAmount)) and book
        // something the user never confirmed — require an explicit value.
        if (!SHAREHOLDER_TYPES.includes(matchType.value) && !(Number(amount) > 0)) {
            Swal.fire({ icon: "warning", text: "Please enter an amount greater than zero." })
            return
        }

        const body = {
            entry_id: activeEntry.id,
            matched_type: matchType.value,
            shareholder_name: shareholderName || null,
            notes: notes || null,
        }

        if (matchType.value === "invoice") {
            if (!selectedInvoice) {
                Swal.fire({ icon: "warning", text: "Please select an invoice." })
                return
            }
            // selectedBank.label is the real payment_mode label (e.g. "HDFC - 50200006048052")
            // selectedBank.value is the numeric tblpayment_finance.id.
            // NOTE: the invoice payment tables only have a string payment_mode
            // column (no bank-id column), so invoice matches persist the label;
            // expense matches persist the numeric id as payment_mode_id.
            // Backend will reverse the INR entry amount to foreign amount using Frankfurter rate
            // for the payment date when the invoice currency is not INR (id 2). See
            // backfill-exchange-rate.js for the same API shape.
            Object.assign(body, {
                invoice_type: invoiceSource,
                invoice_id: selectedInvoice.id,
                payment_mode: selectedBank.label || "Bank",
                payment_mode_id: selectedBank.value,
                amount: Number(amount),
                date: matchDate ? moment(matchDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
                tds: tds || 0,
                transaction_id: transactionRef || activeEntry?.reference_no || null,
            })
        } else if (matchType.value === "expense") {
            if (expenseAction === "new") {
                if (!selectedCategory || !expenseTitle) {
                    Swal.fire({ icon: "warning", text: "Please fill category and title." })
                    return
                }
                Object.assign(body, {
                    expense_action: "new",
                    category: selectedCategory.value,
                    title: expenseTitle,
                    payment_via: 1, // Bank — this row is coming straight off a bank statement
                    payment_method_id: selectedBank.value, // <-- specific bank account id from tblpayment_finance
                    amount: Number(amount),
                    date: matchDate ? moment(matchDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
                    location: expenseLocation?.value || 2,
                })
            } else {
                if (!selectedExpense) {
                    Swal.fire({ icon: "warning", text: "Please select an expense." })
                    return
                }
                Object.assign(body, {
                    expense_action: "existing",
                    expense_id: selectedExpense.value,
                    payment_mode_id: selectedBank.value, // <-- specific bank id, not hardcoded 1
                    payment_method_id: selectedBank.value,
                    payment_via: 1,
                    amount: Number(amount),
                    date: matchDate ? moment(matchDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
                })
            }
        } else if (matchType.value === "overdraft_withdraw" || matchType.value === "overdraft_deposit") {
            if (!odAccount) {
                Swal.fire({ icon: "warning", text: "Please select an overdraft account." })
                return
            }
            Object.assign(body, {
                overdraft_bank_id: odAccount.value,
                amount: Number(amount),
                date: matchDate ? moment(matchDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
            })
        }
        // shareholder_investment / shareholder_return need nothing beyond the
        // shared amount/date/notes already on the entry — acknowledgement only.

        await postMatch(body)
    }

    // Reclassifying a mistake: unmatch first (which reverses the overdraft
    // balance automatically if that's what it was, but does NOT delete an
    // invoice payment or expense record it created — those are real financial
    // records the user reviews/deletes manually if genuinely wrong), then
    // reopen the same match modal on the now-unprocessed entry.
    const onRematch = async entry => {
        const isMap = entry.link_mode === "map"
        const isOverdraft = entry.matched_ref_table === "overdraft_data"
        const isShareholder = !entry.matched_ref_table
        const result = await Swal.fire({
            title: "Rematch this entry?",
            html: isMap
                ? `This will unlink this statement row from <b>${String(entry.matched_ref_table || "").replace(/_/g, " ")} (ID ${entry.matched_ref_id})</b>. Nothing was created for this match, so there is nothing to delete — the linked record itself is left untouched.`
                : isOverdraft
                ? "This will undo the previous classification (reversing the overdraft balance if that's what it was) so you can pick a new one."
                : isShareholder
                ? "This will clear the shareholder acknowledgement so you can pick a new classification."
                : `This will <b>reverse</b> the previous match — the <b>${entry.matched_ref_table.replace(/_/g, " ")}</b> record it created (ID ${entry.matched_ref_id}) will be deleted and dues restored — so you can reclassify it. If that record was changed since matching, you'll be asked to fix it on its own page first.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, unmatch it",
        })
        if (!result.isConfirmed) return

        const res = await FetchNodePostData(setLoading, "bankStatement/unmatch", token, { entry_id: entry.id })
        if (res?.data?.success) {
            setInvoiceLists({ zithas: null, ztpl: null })
            await fetchEntries(selectedBank?.bank_name)
            fetchSummary()
            fetchBankCounts()
            // Report exactly what the reversal did (deleted payment, restored
            // due, warnings) before reopening the match modal — the entry is
            // no longer linked to anything at this point.
            const lines = [...(res?.data?.reversed || [])]
            if (res?.data?.warning) lines.push(res.data.warning)
            const confirm = await Swal.fire({
                icon: res?.data?.warning ? "warning" : "success",
                title: "Entry unmatched",
                html: (lines.length ? lines.map(l => `<div>${l}</div>`).join("") + "<br/>" : "") + "Pick a new classification for this entry?",
                showCancelButton: true,
                confirmButtonText: "Reclassify",
                cancelButtonText: "Later",
            })
            if (confirm.isConfirmed) {
                openMatchModal({ ...entry, status: "unprocessed", matched_type: null, matched_ref_id: null, matched_ref_table: null })
            }
        } else {
            Swal.fire({ icon: "error", text: res?.data?.message || "Could not unmatch this entry." })
        }
    }

    const bankMappedCount = tabCounts.matched
    const bankUnmatchedCount = tabCounts.unmatched

    // Filtering/pagination now happen server-side (see fetchEntries) — `entries`
    // IS the current page already, `total` is the full filtered count from the
    // API.
    const pageEntries = entries
    const totalPages = Math.max(1, Math.ceil(total / pageSize))
    const currentPage = Math.min(page, totalPages)

    useEffect(() => {
        setPage(1)
    }, [debouncedSearch, selectedBank, pageSize, activeTab])

    // Windowed page numbers (1 … p-1 p p+1 … last) instead of listing every
    // page — with thousands of entries that flat list overflowed the whole
    // page horizontally and was unusable.
    const pageNumbers = useMemo(() => {
        const delta = 2
        const range = []
        const withDots = []
        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
                range.push(i)
            }
        }
        let last = 0
        for (const i of range) {
            if (last && i - last > 1) withDots.push("...")
            withDots.push(i)
            last = i
        }
        return withDots
    }, [totalPages, currentPage])

    const { openAdd, openEdit, onDelete, entryModal } = useStatementEntryActions({
        apiBase: "bankStatement",
        account: selectedBank,
        nameKey: "bank_name",
        idKey: "bank_id",
        accountWord: "bank",
        token,
        setLoading,
        onChanged: () => {
            fetchEntries(selectedBank?.bank_name)
            fetchSummary()
            fetchBankCounts()
        },
    })

    return (
        <React.Fragment>
            {loading && <Loader />}
            <div className="page-content">
                <Container fluid className="bsc-page">
                    <Breadcrumbs title={"Reports"} breadcrumbItem={"Bank Statement Reconsile"} />


                    <div className="bsc-stats">
                        <div className="bsc-stat-card">
                            <div className="bsc-stat-icon total">
                                <i className="fas fa-list-ul"></i>
                            </div>
                            <div>
                                <div className="bsc-stat-label">Total Entries (all banks)</div>
                                <div className="bsc-stat-value">{summary.total}</div>
                            </div>
                        </div>
                        <div className="bsc-stat-card">
                            <div className="bsc-stat-icon mapped">
                                <i className="fas fa-check-circle"></i>
                            </div>
                            <div>
                                <div className="bsc-stat-label">Mapped Entries</div>
                                <div className="bsc-stat-value">{summary.matched}</div>
                            </div>
                        </div>
                        <div className="bsc-stat-card">
                            <div className="bsc-stat-icon remaining">
                                <i className="fas fa-hourglass-half"></i>
                            </div>
                            <div>
                                <div className="bsc-stat-label">Remaining Entries</div>
                                <div className="bsc-stat-value">{summary.unmatched}</div>
                            </div>
                        </div>
                    </div>

                    <div className="bsc-card">
                        {/* One line: account + file + Import grouped on the left as the
                            upload section, Export on its own at the right edge. */}
                        <div className="bsc-action-row">
                            <div className="bsc-upload-group">
                                <div className="bsc-upload-field">
                                    <Label>Select Bank</Label>
                                    <Select
                                        placeholder="Select Bank"
                                        options={bankOptions}
                                        value={selectedBank}
                                        onChange={setSelectedBank}
                                        formatOptionLabel={formatBankOptionLabel}
                                        styles={colourStyles}
                                    />
                                </div>
                                {canAdd && (
                                    <>
                                        <div className="bsc-upload-field">
                                            <Label>Upload Bank Statement (Excel)</Label>
                                            <Input key={fileKey} type="file" accept=".xlsx,.xls,.csv" onChange={e => setFile(e.target.files[0])} data-testid="statement-file" />
                                        </div>
                                        <button className="bsc-import-btn" onClick={onUpload} data-testid="statement-import">
                                            <i className="fas fa-upload me-2"></i>
                                            Import
                                        </button>
                                    </>
                                )}
                            </div>
                            <div className="bsc-right-actions">
                                {canAdd && (
                                    <button className="bsc-import-btn bsc-export-btn" onClick={openAdd} title="Add a single statement entry" data-testid="statement-add">
                                        <i className="fas fa-plus me-2"></i>
                                        Add Entry
                                    </button>
                                )}
                                <button
                                    className="bsc-import-btn bsc-export-btn"
                                    onClick={() => setExportModal(true)}
                                    title="Export entries by bank, date range and status"
                                    data-testid="statement-export"
                                >
                                    <i className="fas fa-file-excel me-2"></i>
                                    Export
                                </button>
                            </div>
                        </div>
                        {selectedBank && <AccountInfoLine counts={bankCounts?.[selectedBank.bank_name]} />}
                    </div>

                    <div className="bsc-card">
                        <div className="bsc-tabs">
                            <button
                                className={`bsc-tab ${activeTab === "unmatched" ? "active" : ""}`}
                                onClick={() => setActiveTab("unmatched")}
                            >
                                Unmatched {selectedBank ? `(${bankUnmatchedCount})` : ""}
                            </button>
                            <button
                                className={`bsc-tab ${activeTab === "matched" ? "active" : ""}`}
                                onClick={() => setActiveTab("matched")}
                            >
                                Matched {selectedBank ? `(${bankMappedCount})` : ""}
                            </button>
                        </div>

                        <div className="bsc-toolbar">
                            <div className="bsc-search">
                                <i className="fas fa-search"></i>
                                <input
                                    type="text"
                                    placeholder="Search by description or reference..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                            </div>
                            <div className="bsc-page-size">
                                <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
                                    {[10, 25, 50, 100].map(n => (
                                        <option key={n} value={n}>Show {n}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="bsc-table-wrap">
                            <table className="bsc-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Reference</th>
                                        <th>Description</th>
                                        <th className="text-end">Debit</th>
                                        <th className="text-end">Credit</th>
                                        <th className="text-center">Status</th>
                                        <th className="text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageEntries.map(entry => (
                                        <tr key={entry.id} data-testid="statement-row" data-entry-id={entry.id}>
                                            <td>{entry.statement_date ? moment(entry.statement_date).format("DD-MM-YYYY") : ""}</td>
                                            <td>{entry.reference_no}</td>
                                            <td className="bsc-desc" title={entry.description}>{entry.description}</td>
                                            <td className="text-end">{Number(entry.debit) ? `₹ ${Number(entry.debit).toFixed(2)}` : ""}</td>
                                            <td className="text-end">{Number(entry.credit) ? `₹ ${Number(entry.credit).toFixed(2)}` : ""}</td>
                                            <td className="text-center">
                                                {entry.status === "matched" ? (
                                                    <span
                                                        className="bsc-pill matched"
                                                        title={[entry.shareholder_name, entry.notes].filter(Boolean).join(" — ") || undefined}
                                                    >
                                                        <i className="fas fa-check"></i>
                                                        {MATCH_TYPE_LABELS[entry.matched_type] || entry.matched_type}
                                                    </span>
                                                ) : (
                                                    <span className="bsc-pill unprocessed">
                                                        <i className="fas fa-clock"></i>
                                                        Unprocessed
                                                    </span>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                {entry.status !== "matched" ? (
                                                    <div className="bsc-row-actions">
                                                        <button className="bsc-match-btn" title="Match" onClick={() => openMatchModal(entry)} data-testid="row-match">
                                                            <i className="fas fa-handshake"></i>
                                                        </button>
                                                        {/* Edit/Delete only while unmatched — a matched row
                                                            has booked records from its date and amount. */}
                                                        {canEdit && (
                                                            <button className="bsc-match-btn edit" title="Edit" onClick={() => openEdit(entry)} data-testid="row-edit">
                                                                <i className="fas fa-pen"></i>
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button className="bsc-match-btn delete" title="Delete" onClick={() => onDelete(entry)} data-testid="row-delete">
                                                                <i className="fas fa-trash-alt"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <button className="bsc-match-btn rematch" title="Rematch" onClick={() => onRematch(entry)}>
                                                        <i className="fas fa-rotate"></i>
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    {pageEntries.length === 0 && (
                                        <tr>
                                            <td colSpan={7} className="text-center">
                                                {!selectedBank
                                                    ? "Select a bank to view its statement."
                                                    : total > 0
                                                        ? "" // between pages while a fetch is in flight
                                                        : debouncedSearch
                                                            ? "No entries match your search."
                                                            : activeTab === "matched"
                                                                ? "No matched entries yet."
                                                                : "No unmatched entries for this bank yet."}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {total > 0 && (
                            <div className="bsc-pagination">
                                <div className="bsc-pagination-info">
                                    Showing {(currentPage - 1) * pageSize + 1} to{" "}
                                    {Math.min(currentPage * pageSize, total)} of {total} entries
                                </div>
                                <div className="bsc-pagination-controls">
                                    <button
                                        className="bsc-page-btn"
                                        disabled={currentPage === 1}
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                    </button>
                                    {pageNumbers.map((n, i) =>
                                        n === "..." ? (
                                            <span key={`dots-${i}`} className="bsc-page-dots">…</span>
                                        ) : (
                                            <button
                                                key={n}
                                                className={`bsc-page-btn ${n === currentPage ? "active" : ""}`}
                                                onClick={() => setPage(n)}
                                            >
                                                {n}
                                            </button>
                                        )
                                    )}
                                    <button
                                        className="bsc-page-btn"
                                        disabled={currentPage === totalPages}
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    >
                                        <i className="fas fa-chevron-right"></i>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </Container>
            </div>

            <Modal isOpen={matchModal} size="xl" centered toggle={() => setMatchModal(false)}>
                <div className="modal-header">
                    <h5 className="modal-title mt-0">Match Entry</h5>
                    <button type="button" onClick={() => setMatchModal(false)} className="btn-modal-close">
                        <span className="btn-modal-span">&times;</span>
                    </button>
                </div>
                <div className="modal-body">
                    <Row className="mb-3">
                        <Col md={6}>
                            <Label>Description</Label>
                            <div className="text-muted">{activeEntry?.description}</div>
                            {selectedBank && <small className="text-muted">Bank: {selectedBank.label} (id {selectedBank.value}, via 1)</small>}
                        </Col>
                        <Col md={3}>
                            <Label>Amount (₹)</Label>
                            <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} disabled={matchMode === "map" || SHAREHOLDER_TYPES.includes(matchType?.value)} />
                            {matchMode === "map" || SHAREHOLDER_TYPES.includes(matchType?.value) ? (
                                <small className="text-muted">Not used for this match type — the statement's own amount (₹{Number(amount).toFixed(2)}) is recorded as-is.</small>
                            ) : (
                                <small className="text-muted">Entry is in INR — for foreign-currency invoices the backend will reverse-convert using Frankfurter rate for the payment date (₹ / rate).</small>
                            )}
                        </Col>
                        <Col md={3}>
                            <Label>Date</Label>
                            <Input type="date" value={matchDate} onChange={e => setMatchDate(e.target.value)} disabled={matchMode === "map" || SHAREHOLDER_TYPES.includes(matchType?.value)} />
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={12}>
                            <Label>This entry belongs to ({isCreditEntry ? "Credit" : "Debit"})</Label>
                            <Select options={matchTypeOptions} value={matchType} onChange={onSelectMatchType} styles={colourStyles} />
                        </Col>
                    </Row>

                    {matchType?.value === "invoice" && (
                        <>
                            <Row className="mb-3">
                                <Col md={3}>
                                    <Label>Invoice Type</Label>
                                    <div className="d-flex gap-3 mt-2">
                                        <div>
                                            <input
                                                type="radio"
                                                id="src-zithas"
                                                checked={invoiceSource === "zithas"}
                                                onChange={() => onChangeInvoiceSource("zithas")}
                                            />
                                            <label htmlFor="src-zithas" className="ms-1">Zithas</label>
                                        </div>
                                        <div>
                                            <input
                                                type="radio"
                                                id="src-ztpl"
                                                checked={invoiceSource === "ztpl"}
                                                onChange={() => onChangeInvoiceSource("ztpl")}
                                            />
                                            <label htmlFor="src-ztpl" className="ms-1">ZTPL</label>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={9}>
                                    <Label>Invoice</Label>
                                    <Select
                                        options={invoiceSelectOptions}
                                        value={selectedInvoice}
                                        onChange={onPickInvoice}
                                        formatOptionLabel={formatMatchOption}
                                        styles={colourStyles}
                                    />
                                </Col>
                            </Row>

                            {selectedInvoice && (
                                matchMode === "map" ? (
                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <div className="bsc-map-banner">
                                                <i className="fas fa-circle-info me-2"></i>
                                                This invoice is already fully paid (due ₹0) — this statement row will only be <b>mapped</b> to <b>{selectedInvoice.label}</b>. No new payment will be created.
                                            </div>
                                            <button type="button" className="btn btn-purple btn-label" onClick={onMapToTarget}>
                                                <i className="fas fa-link me-2 icon-size label-icon"></i>
                                                Map to This Invoice
                                            </button>
                                        </Col>
                                    </Row>
                                ) : (
                                    <Row className="mb-3">
                                        {selectedInvoice.foreign && (
                                            <Col md={12} className="mb-3">
                                                {(() => {
                                                    // Same fallback order as the backend: the day's
                                                    // rate, else the invoice's own stored rate.
                                                    const rate = fx.rate || selectedInvoice.rate
                                                    const sym = selectedInvoice.symbol
                                                    const cur = selectedInvoice.currency
                                                    if (fx.loading) return <div className="bsc-fx-box">Fetching the {cur} rate for this date…</div>
                                                    if (!rate) {
                                                        return (
                                                            <div className="bsc-fx-box warn">
                                                                No {cur} → INR rate is available for this date or on the invoice, so the amount cannot be converted. Please do not confirm this match until a rate is available.
                                                            </div>
                                                        )
                                                    }
                                                    const booked = Number((Number(amount || 0) / rate).toFixed(2))
                                                    const dueAfter = Math.max(0, selectedInvoice.due - booked - (Number(tds) || 0))
                                                    return (
                                                        <div className="bsc-fx-box">
                                                            <div><span>Statement amount</span><b>{formatInr(amount)}</b></div>
                                                            <div>
                                                                <span>Rate on {moment(matchDate).format("DD-MM-YYYY")}{fx.rate ? "" : " (not available — invoice rate used)"}</span>
                                                                <b>1 {cur} = {formatInr(rate)}</b>
                                                            </div>
                                                            <div><span>Will be booked on the invoice</span><b>{sym}{booked.toFixed(2)} {cur}</b></div>
                                                            <div>
                                                                <span>Invoice due now</span>
                                                                <b>{sym}{selectedInvoice.due.toFixed(2)} {cur}{Number.isFinite(selectedInvoice.dueInr) ? ` ≈ ${formatInr(selectedInvoice.dueInr)}` : ""}</b>
                                                            </div>
                                                            <div><span>Due after this payment{Number(tds) ? " and TDS" : ""}</span><b>{sym}{dueAfter.toFixed(2)} {cur}</b></div>
                                                        </div>
                                                    )
                                                })()}
                                            </Col>
                                        )}
                                        <Col md={4}>
                                            <Label>TDS</Label>
                                            <Input type="number" value={tds} onChange={e => setTds(e.target.value)} placeholder="0" />
                                        </Col>
                                        <Col md={8}>
                                            <Label>Transaction Ref</Label>
                                            <Input
                                                type="text"
                                                value={transactionRef}
                                                onChange={e => setTransactionRef(e.target.value)}
                                                placeholder="Cheque / UTR / transaction number"
                                            />
                                        </Col>
                                    </Row>
                                )
                            )}
                        </>
                    )}

                    {matchType?.value === "expense" && (
                        <>
                            <Row className="mb-3">
                                <Col md={12}>
                                    <div className="d-flex gap-3">
                                        <div>
                                            <input
                                                type="radio"
                                                id="exp-new"
                                                checked={expenseAction === "new"}
                                                onChange={() => setExpenseAction("new")}
                                            />
                                            <label htmlFor="exp-new" className="ms-1">Add New Expense</label>
                                        </div>
                                        <div>
                                            <input
                                                type="radio"
                                                id="exp-existing"
                                                checked={expenseAction === "existing"}
                                                onChange={() => setExpenseAction("existing")}
                                            />
                                            <label htmlFor="exp-existing" className="ms-1">Pay Registered Expense</label>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            {expenseAction === "new" ? (
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <Label>Category</Label>
                                        <Select options={categoryOptions} value={selectedCategory} onChange={setSelectedCategory} styles={colourStyles} />
                                    </Col>
                                    <Col md={5}>
                                        <Label>Title</Label>
                                        <Input type="text" value={expenseTitle} onChange={e => setExpenseTitle(e.target.value)} />
                                    </Col>
                                    <Col md={3}>
                                        <Label>Location</Label>
                                        <Select options={getLocations} value={expenseLocation} onChange={setExpenseLocation} styles={colourStyles} />
                                    </Col>
                                </Row>
                            ) : (
                                <>
                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Label>Expense</Label>
                                            <Select
                                                options={expenseSelectOptions}
                                                value={selectedExpense}
                                                onChange={setSelectedExpense}
                                                formatOptionLabel={formatMatchOption}
                                                styles={colourStyles}
                                            />
                                        </Col>
                                    </Row>

                                    {selectedExpense && (
                                        <>
                                            {/* Expense is never forced — you can still add another payment
                                                against it even after due hits ₹0, so this toggle always stays,
                                                just defaulted by due (handled in the effect above). */}
                                            <Row className="mb-3">
                                                <Col md={12}>
                                                    <div className="bsc-tabs" style={{ marginBottom: 0 }}>
                                                        <button
                                                            type="button"
                                                            className={`bsc-tab ${matchMode === "create" ? "active" : ""}`}
                                                            onClick={() => setMatchMode("create")}
                                                        >
                                                            Add New Payment
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`bsc-tab ${matchMode === "map" ? "active" : ""}`}
                                                            onClick={() => setMatchMode("map")}
                                                        >
                                                            Map to Existing Payment
                                                        </button>
                                                    </div>
                                                    <small className="text-muted d-block mt-1">
                                                        {matchMode === "map"
                                                            ? "This statement row will be LINKED to this expense — nothing new gets created."
                                                            : Math.abs(Number(selectedExpense.due) || 0) < 0.01
                                                                ? "Heads up — this expense already shows ₹0 due. Adding a new payment records an extra amount against it. If this statement row is the original payment, use Map instead."
                                                                : "This will record a new payment against this expense."}
                                                    </small>
                                                </Col>
                                            </Row>

                                            {matchMode === "map" && (
                                                <Row className="mb-3">
                                                    <Col md={12}>
                                                        <button type="button" className="btn btn-purple btn-label" onClick={onMapToTarget}>
                                                            <i className="fas fa-link me-2 icon-size label-icon"></i>
                                                            Map to This Expense
                                                        </button>
                                                    </Col>
                                                </Row>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {(matchType?.value === "overdraft_withdraw" || matchType?.value === "overdraft_deposit") && (
                        <>
                            <Row className="mb-3">
                                <Col md={12}>
                                    <Label>Overdraft Account</Label>
                                    <Select options={odBanks} value={odAccount} onChange={setOdAccount} styles={colourStyles} />
                                </Col>
                            </Row>

                            {odAccount && (
                                matchMode === "map" ? (
                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <div className="bsc-map-banner">
                                                <i className="fas fa-circle-info me-2"></i>
                                                {matchType.value === "overdraft_withdraw"
                                                    ? `This withdrawal (₹${entryAmount.toFixed(2)}) exceeds the available limit (₹${Number(odAccount.bal_limit).toFixed(2)}) on this account`
                                                    : `This return (₹${entryAmount.toFixed(2)}) exceeds what's currently owed (₹${Number(odAccount.used_limit).toFixed(2)}) on this account`}
                                                {" "}— it can only be <b>mapped</b> to this overdraft account, not created fresh.
                                            </div>
                                            <button type="button" className="btn btn-purple btn-label" onClick={onMapToTarget}>
                                                <i className="fas fa-link me-2 icon-size label-icon"></i>
                                                Map to This Account
                                            </button>
                                        </Col>
                                    </Row>
                                ) : (
                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <small className="text-muted">
                                                This will create a new overdraft {matchType.value === "overdraft_withdraw" ? "withdrawal" : "return"} entry for ₹{entryAmount.toFixed(2)} against this account.
                                            </small>
                                        </Col>
                                    </Row>
                                )
                            )}
                        </>
                    )}

                    {SHAREHOLDER_TYPES.includes(matchType?.value) && (
                        <Row className="mb-3">
                            <Col md={12}>
                                <Label>Shareholder Name</Label>
                                <Input
                                    type="text"
                                    placeholder="Optional — for reference only"
                                    value={shareholderName}
                                    onChange={e => setShareholderName(e.target.value)}
                                />
                            </Col>
                        </Row>
                    )}

                    {matchType && (
                        <Row className="mb-3">
                            <Col md={12}>
                                <Label>Notes</Label>
                                <Input
                                    type="textarea"
                                    rows={2}
                                    placeholder="Optional — kept on this entry for acknowledgement, not required to save"
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                />
                            </Col>
                        </Row>
                    )}
                </div>
                <div className="modal-footer">
                    <button className="btn btn-red btn-label me-1" onClick={() => setMatchModal(false)}>
                        <i className="dripicons-cross me-2 icon-center label-icon"></i>
                        Cancel
                    </button>
                    {/* Map submissions happen via onMapToTarget's own "Map" button above —
                        this button only ever books a "create" (or shareholder
                        acknowledgement), so it's hidden once matchMode is "map". */}
                    {matchMode === "map" ? (
                        <span className="text-muted" style={{ fontSize: 13, alignSelf: "center" }}>
                            Use the Map button above to complete this match.
                        </span>
                    ) : (
                        <button className="btn btn-purple btn-label" onClick={onSubmitMatch}>
                            <i className="fas fa-check me-2 icon-size label-icon"></i>
                            Confirm Match
                        </button>
                    )}
                </div>
            </Modal>

            {entryModal}

            <StatementExportModal
                isOpen={exportModal}
                toggle={() => setExportModal(false)}
                token={token}
                setLoading={setLoading}
                endpoint="bankStatement/export/entries"
                idsParam="bank_ids"
                accountLabel="Bank"
                sourceKey="bank_name"
                accountOptions={bankOptions}
                defaultAccount={selectedBank}
                typeLabels={MATCH_TYPE_LABELS}
                filePrefix="Bank_Statement"
            />
        </React.Fragment>
    )
}

export default BankStatementConsole
